import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
  DeepMap,
  FieldError,
  FieldValues,
  UseControllerProps,
  useController,
  useWatch
} from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Autocomplete, {
  AutocompleteRenderInputParams
} from "@mui/material/Autocomplete";
import MuiCircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";

import { useDebounce } from "hooks";
import { get } from "lodash";

import {
  MASTER_PRODUCT_ALLOWLIST_FILTER_FEATURE,
  MULTIPLE_CPT_CODES_FEATURE
} from "constants/feature-flags";
import {
  useGetMasterProductsQuery,
  useLazyGetMasterProductQuery
} from "services/master-products";
import { MasterProduct } from "services/types/master-products.type";

import AddMasterProductForm from "./add-master-product-form";
import MasterProductListItem from "./master-product-list-item.component";

type MasterProductFieldType<T extends FieldValues> = {
  required?: boolean;
  type: number;
  subtype?: number | number[];
  label: string;
  placeholder?: string;
  autofocus?: boolean;
  icon?: ReactNode;
  sx?: SxProps;
  initialSearch?: string;
  clearAfterSelect?: boolean;
  allowProductAddition?: boolean;
  onSelected?: (product: MasterProduct) => void;
  disabled?: boolean;
  newProduct?: any;
  disableTypeSelection?: boolean;
  disableSubTypeSelection?: boolean;
} & UseControllerProps<T>;

// MasterProduct.cpt_codes is gated behind the flag for deploy-ordering safety.
const getMasterProductQuery = (multipleCptCodesFeature?: boolean): string => {
  const cptCodes = multipleCptCodesFeature
    ? ",cpt_codes{id,code,description}"
    : "";
  return `{id,cpt_code{id,code,description}${cptCodes},manufacturer,specification,model,type,subtype,description,display_name,is_active}`;
};

const getOptions = (
  options: MasterProduct[],
  allowProductAddition?: boolean
) => {
  if (!allowProductAddition) return options;
  return [
    ...options,
    {
      id: -1
    } as any as MasterProduct
  ];
};

export default function MasterProductField<T extends FieldValues>({
  control,
  name,
  required = false,
  label,
  placeholder,
  type,
  subtype,
  initialSearch = "",
  newProduct,
  icon,
  autofocus = false,
  sx,
  clearAfterSelect = false,
  allowProductAddition = false,
  disableTypeSelection = false,
  disableSubTypeSelection = false,
  onSelected,
  disabled
}: MasterProductFieldType<T>) {
  const [openAddForm, setOpenAddForm] = useState<boolean>(false);
  const [newProductDescription, setNewProductDescription] =
    useState<string>(initialSearch);
  const [search, setSearch] = useState<string>(initialSearch);
  const [options, setOptions] = useState<MasterProduct[]>([]);
  const [open, setOpen] = useState<boolean>(autofocus);
  const { value: dSearch, isDebouncing } = useDebounce(search);
  const flags = useFlags();
  const multipleCptCodesFeature = Boolean(flags[MULTIPLE_CPT_CODES_FEATURE]);
  const allowlistFilterFeature = Boolean(
    flags[MASTER_PRODUCT_ALLOWLIST_FILTER_FEATURE]
  );
  const {
    field: { onChange, onBlur, ...restField },
    formState: { errors }
  } = useController<T>({
    name,
    control,
    rules: required
      ? {
          required: `${label} is required`
        }
      : undefined
  });
  const errorMessage =
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;

  const fieldValue = useWatch({ control, name });
  const renderInput = React.useCallback(
    ({ InputProps, ...restParams }: AutocompleteRenderInputParams) => (
      <TextField
        {...restParams}
        InputProps={{
          ...InputProps,
          startAdornment: icon ? (
            <InputAdornment position="start">{icon}</InputAdornment>
          ) : undefined
        }}
        sx={sx}
        InputLabelProps={{ disabled: false }}
        required={required}
        error={Boolean(errorMessage)}
        helperText={errorMessage || ""}
        label={label}
        autoFocus={open}
        placeholder={placeholder}
        disabled={disabled}
      />
    ),
    [errorMessage, open, sx, icon, placeholder, label, required, disabled]
  );

  const {
    data: masterProducts,
    isLoading,
    isSuccess,
    refetch
  } = useGetMasterProductsQuery({
    search: dSearch,
    type,
    subtype,
    // Omitted (not `false`) while the flag is off, so the request stays byte-identical.
    practice_filtered: allowlistFilterFeature || undefined,
    query: getMasterProductQuery(multipleCptCodesFeature)
  });

  const [getMasterProductById] = useLazyGetMasterProductQuery();

  useEffect(() => {
    if (Boolean(fieldValue)) {
      setSearch(
        `${fieldValue.display_name} ${
          fieldValue.cpt_code?.code ? `(${fieldValue.cpt_code?.code})` : ""
        }`
      );
    } else {
      setSearch("");
    }
  }, [fieldValue]);

  useEffect(() => {
    if (masterProducts) {
      setOptions(getOptions(masterProducts, allowProductAddition));
    } else {
      setOptions([]);
    }
  }, [masterProducts, allowProductAddition]);

  const handleChange = useCallback(
    (
      data:
        | NonNullable<string | MasterProduct>
        | (string | MasterProduct)[]
        | null
    ) => {
      if ((data as any)?.id === -1) {
        setOpenAddForm(true);
        return;
      }
      onChange(data);
      if (Boolean(data)) {
        setSearch((data as MasterProduct).display_name || "");
        if (onSelected) {
          onSelected(data as MasterProduct);
          if (clearAfterSelect) {
            setSearch("");
          }
        }
      }
      onBlur();
    },
    [clearAfterSelect, onBlur, onChange, onSelected]
  );

  const newMasterProduct = useMemo(
    () => ({
      ...newProduct,
      description: newProductDescription
    }),
    [newProduct, newProductDescription]
  );

  const handleSave = useCallback(
    async (product: MasterProduct) => {
      refetch();

      const { data: newProduct } = await getMasterProductById(product.id);
      handleChange(newProduct as MasterProduct);

      setOpenAddForm(false);
    },
    [getMasterProductById, handleChange, refetch]
  );

  const handleCancel = useCallback(() => {
    setOpenAddForm(false);
  }, []);

  if (!isSuccess) {
    return <MuiCircularProgress />;
  }

  return (
    <>
      <Autocomplete
        disablePortal
        loading={isLoading || isDebouncing}
        fullWidth
        disabled={disabled}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        open={open}
        options={options}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          return option?.display_name ?? "";
        }}
        renderOption={(props: object, option: MasterProduct) => (
          <MasterProductListItem
            props={props}
            option={option}
            search={search.trim()}
            key={option.id}
            isNew={option.id === -1}
          />
        )}
        isOptionEqualToValue={(option: MasterProduct, value: MasterProduct) =>
          option.id === value.id
        }
        inputValue={search}
        onInputChange={(event, newInputValue, reason) => {
          if (Boolean(event)) {
            if (reason === "input" || reason === "reset") {
              setSearch(newInputValue);
            } else {
              setSearch("");
            }
          }
        }}
        popupIcon={<ArrowDropDownIcon />}
        renderInput={renderInput}
        filterOptions={(x) => x}
        onChange={(_, data) => {
          setNewProductDescription(search);
          handleChange(data);
        }}
        onBlur={onBlur}
        {...restField}
      />
      <AddMasterProductForm
        isOpen={openAddForm}
        onCancel={handleCancel}
        onSave={handleSave}
        newProduct={newMasterProduct}
        disableTypeSelection={disableTypeSelection}
        disableSubTypeSelection={disableSubTypeSelection}
      />
    </>
  );
}
